/**
 * Support Menu Button (Feature 104; formerly AsanaHelpButton — the URL is an
 * env-configured support form, not Asana specifically).
 *
 * Fixed help button, bottom-right, every page. For the care-seeker audience
 * (care seekers + logged-out visitors) it opens a two-choice menu: find care
 * (Care Concierge contact form) or report a platform problem (support form).
 * Professionals and admins keep today's direct-open behavior — no menu.
 */
import { useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import {
  useConciergeChooser,
} from '@/features/care-concierge/chooser/ConciergeChooserProvider';

const SUPPORT_FORM_URL = import.meta.env.VITE_SUPPORT_FORM_URL;

const openSupportForm = () => {
  const newWindow = window.open(SUPPORT_FORM_URL, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
};

export function SupportMenuButton() {
  const { user } = useAuth();
  const { openConcierge } = useConciergeChooser();
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isCareSeekerAudience = !user || user.role === 'CARE_SEEKER';

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const handleButtonClick = () => {
    if (!isCareSeekerAudience) {
      openSupportForm();
      return;
    }
    setMenuOpen((open) => !open);
  };

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-50">
      {menuOpen && (
        <div className="absolute bottom-14 right-0 w-72 rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
          <button
            type="button"
            className="w-full rounded-lg bg-[#026857]/10 px-3 py-2.5 text-left text-sm font-semibold text-[#026857] hover:bg-[#026857]/15"
            onClick={() => {
              setMenuOpen(false);
              openConcierge('help-menu');
            }}
          >
            Need help finding the right care? →
          </button>
          <button
            type="button"
            className="mt-1 w-full rounded-lg px-3 py-2.5 text-left text-sm text-gray-600 hover:bg-gray-50"
            onClick={() => {
              setMenuOpen(false);
              openSupportForm();
            }}
          >
            Need to report a problem with the platform?
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={handleButtonClick}
        className="flex items-center gap-2 rounded-full bg-[#026857] px-4 py-3 text-white shadow-lg transition-all duration-200 hover:scale-105 hover:bg-[#025247] focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        aria-label="Get help"
        aria-expanded={isCareSeekerAudience ? menuOpen : undefined}
        title="Need help?"
      >
        <HelpCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Help</span>
      </button>
    </div>
  );
}
